import { CaretDownOutlined, CaretUpOutlined, UndoOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type TDisplayColumnData } from 'components/data/types';
import { Tag, Tooltip } from 'components/ui';
import { type AtlasGqlTableView } from 'types/atlas-graphql';
import { useAccountContext } from 'utils/account/AccountContext';

import { DataTableContext } from '../index';
import { type TDataTableStorageKey } from '../types';
import { tableStorage } from '../utils';
import { columnPickerStorage, updateCustomCols } from '../Views/ColumnPicker/utils';
import { type ConditionsState, ConditionsModal } from '../Views/Conditions';
import { multiSorterStorage, updateSort } from '../Views/MultiSorter/utils';
import { type SaveViewState, ESaveOptions, SaveViewModal } from '../Views/SaveView';
import {
  isViewAndTableStateEqual,
  mapColumns,
  mapMultiSorts,
  removeDefaultFilters,
  updateView,
} from '../Views/utils';

import {
  ItalicText,
  StyledBanner,
  StyledButton,
  StyledLink,
  StyledTitle,
} from './CustomizedDataBanner.style';

type CustomizedDataBannerProps = {
  tableId?: string;
  storageKey?: TDataTableStorageKey;
  columnDefs: TDisplayColumnData[];
  onTableChange: (pagination: any, filters: any, sorter: any, extra?: any) => void;
  onClearSortAndFilter: () => void;
  onClearSelection: () => void;
  showSavedViews: boolean;
  savedViewCustomFields?: any;
  onSavedViewChange?: (tableView: AtlasGqlTableView) => void;
  additionalBannerMessage?: string;
};

export const CustomizedDataBanner = ({
  tableId,
  storageKey,
  columnDefs,
  onTableChange,
  onClearSortAndFilter,
  onClearSelection,
  showSavedViews,
  savedViewCustomFields,
  onSavedViewChange,
  additionalBannerMessage,
}: CustomizedDataBannerProps) => {
  const { t } = useTranslation();
  const [saveViewState, setSaveViewState] = useState<SaveViewState>({
    isOpen: false,
  });
  const [conditionsState, setConditionsState] = useState<ConditionsState>({
    isOpen: false,
  });

  const { user } = useAccountContext();

  const tableViewStorageManager = tableStorage({ storageKey: `${tableId}_VIEW`, user });
  const columnPickerStorageManager = columnPickerStorage({ storageKey: storageKey?.COLUMN_PICKER });
  const multiSorterStorageManager = multiSorterStorage({ storageKey: storageKey?.MULTI_SORT });

  const {
    pagination,
    filteredInfo: _filteredInfo,
    sortedInfo,
    tableView,
    updateTableView,
    customizedColumnSet,
    updateCustomizedColumnSet,
    multiSortDef,
    updateMultiSortDef,
  } = useContext(DataTableContext) || {};
  const filteredInfo = removeDefaultFilters(tableId, _filteredInfo);

  // checks to see if the view and the table state are different
  const isViewSaved = useMemo(() => {
    return isViewAndTableStateEqual(tableView, {
      filters: filteredInfo,
      sorts: sortedInfo,
      columns: customizedColumnSet,
      multiSorts: multiSortDef,
    });
  }, [tableView, filteredInfo, sortedInfo, customizedColumnSet, multiSortDef]);

  // sets table to given view OR to its default state
  const applyViewToTable = (tableViewToApply?: AtlasGqlTableView) => {
    if (tableViewToApply) {
      const { filters, sorts, columns, multiSorts } = tableViewToApply;

      const sortKey = sorts?.columnKey;
      const appliedSort = sortKey ? { ...sorts, column: { sortKey } } : {};

      // set filters/sorters
      onTableChange({ ...pagination, current: 1 }, filters, appliedSort);
      // set customized columns
      updateCustomCols(columnDefs, columns, updateCustomizedColumnSet, columnPickerStorageManager);
      // set multisort
      updateSort(columnDefs, multiSorts, updateMultiSortDef, multiSorterStorageManager);

      // update view
      updateView(tableViewToApply, updateTableView, tableViewStorageManager);
      onSavedViewChange && onSavedViewChange(tableViewToApply);
    } else {
      // clears filters/sorts
      onClearSortAndFilter();
      // reset customized columns
      updateCustomizedColumnSet(undefined);
      columnPickerStorageManager.remove();
      // reset multisort
      updateMultiSortDef(undefined);
      multiSorterStorageManager.remove();

      // reset table view
      updateTableView(undefined);
      tableViewStorageManager.remove();
    }

    // clear row selections any time the view changes
    onClearSelection();
  };

  // saves changes to the current view OR copy of the current view
  const handleSave = (type: ESaveOptions) => {
    setSaveViewState({
      isOpen: true,
      tableId,
      tableView: {
        ...tableView,
        filters: filteredInfo,
        sorts: sortedInfo,
        columns: (customizedColumnSet ?? []).map(mapColumns),
        multiSorts: (multiSortDef ?? []).map(mapMultiSorts),
        customFields: savedViewCustomFields,
      },
      columnDefs,
      type,
    });
  };

  // shows the conditions modal
  const handleConditions = () => {
    setConditionsState({
      isOpen: true,
      tableView: {
        filters: filteredInfo,
        sorts: sortedInfo,
        columns: (customizedColumnSet ?? []).map(mapColumns),
        multiSorts: (multiSortDef ?? []).map(mapMultiSorts),
      },
      columnDefs,
    });
  };

  return (
    <>
      <StyledBanner>
        <div>
          {tableView ? (
            <>
              <StyledTitle>{tableView.name}</StyledTitle>
              {!isViewSaved && (
                <>
                  <ItalicText>{t('components.viewModified', '(Modified)')}</ItalicText>
                  <Tooltip
                    title={t(
                      'components.resetViewToSaved',
                      'Reset current View to the saved version.'
                    )}
                  >
                    <StyledButton
                      icon={<UndoOutlined />}
                      onClick={() => applyViewToTable(tableView)}
                    />
                  </Tooltip>
                </>
              )}
            </>
          ) : (
            <>
              {customizedColumnSet && multiSortDef ? (
                <StyledTitle>
                  {t(
                    'components.columnsCustomizedAndSorted',
                    'Visible columns are customized and multi-column sort applied:'
                  )}
                </StyledTitle>
              ) : customizedColumnSet ? (
                <StyledTitle>
                  {t('components.columnsCustomized', 'Visible columns are customized')}
                </StyledTitle>
              ) : multiSortDef ? (
                <StyledTitle>
                  {t('components.multiColumnSortApplied', 'Multi-column sort applied:')}
                </StyledTitle>
              ) : null}
              {multiSortDef && (
                <>
                  {multiSortDef.map(({ column: { title }, sortDirection }, index) => (
                    <Tag key={index}>
                      {title}
                      {sortDirection === 'ASC' ? <CaretUpOutlined /> : <CaretDownOutlined />}
                    </Tag>
                  ))}
                </>
              )}
            </>
          )}
          {additionalBannerMessage ? (
            <>
              {(tableView || customizedColumnSet || multiSortDef) && <br />}
              {additionalBannerMessage}
            </>
          ) : null}
        </div>
        <div>
          {showSavedViews && tableView && !tableView.isGlobalView && !isViewSaved && (
            <>
              <Tooltip
                title={t('components.saveChangesToView', 'Save changes to the current View.')}
              >
                <StyledLink type="link" onClick={() => handleSave(ESaveOptions.UPDATE)}>
                  {t('common.save', 'Save')}
                </StyledLink>
              </Tooltip>
              <Divider type="vertical" />
            </>
          )}
          {showSavedViews && tableView && (!isViewSaved || tableView?.isGlobalView) && (
            <>
              <Tooltip title={t('components.saveCopyOfView', 'Save a copy of the current View.')}>
                <StyledLink type="link" onClick={() => handleSave(ESaveOptions.COPY)}>
                  {t('components.saveAs', 'Save As')}
                </StyledLink>
              </Tooltip>
              <Divider type="vertical" />
            </>
          )}
          <Tooltip title={t('components.viewTableConditions', 'View the conditions of the table.')}>
            <StyledLink type="link" onClick={handleConditions}>
              {t('table.conditions', 'Conditions')}
            </StyledLink>
          </Tooltip>
          <Divider type="vertical" />
          <Tooltip
            title={t('components.resetTableToDefault', 'Resets the table to its default state.')}
          >
            <StyledLink type="link" onClick={() => applyViewToTable(undefined)}>
              {t('components.resetTable', 'Reset Table')}
            </StyledLink>
          </Tooltip>
        </div>
      </StyledBanner>
      <ConditionsModal
        conditionsState={conditionsState}
        onClose={() => setConditionsState({ isOpen: false })}
      />
      {showSavedViews && !!tableView && (
        <SaveViewModal
          saveViewState={saveViewState}
          onClose={() => setSaveViewState({ isOpen: false })}
          onSuccess={savedView => {
            if (savedView) {
              applyViewToTable(savedView);
            }
          }}
        />
      )}
    </>
  );
};
